import {
  getSiloV2MarketEntry,
  getSiloV3MarketEntry,
  siloMarkets,
  siloMarketsV3,
  siloV2LenderKey,
  siloV3LenderKey,
  type SiloMarketEntry,
} from '@1delta/data-sdk'
import { isSiloV3Type } from '../../../utils'

/**
 * One entry of the roster a Silo read covers: the pair, and the per-pair lender
 * key its results must be published under.
 *
 * Silo publishes ONE lender key per (silo0, silo1) pair — 122 of them on Ethereum,
 * 85 on Arbitrum — and every one of those keys used to be its own QUERY, i.e. its
 * own 10-call slice. That is what makes Silo the heaviest read we serve (1,220 of
 * Ethereum's ~3,000 user calls) and it is also why the lens could not be dropped in
 * as it was for LlamaLend: a per-key query would have issued the lens call 122
 * times over. The queries are now collapsed upstream, so a read covers a ROSTER and
 * this is the type that carries it.
 */
export interface SiloRosterEntry {
  key: string
  market: SiloMarketEntry
}

/**
 * Resolve the pairs a query covers.
 *
 * Handles both shapes deliberately, because both occur:
 *
 *  - the COLLAPSED query (`SILO_V2` with `params` listing per-pair keys), which is
 *    what production issues after `organizeUserQueries` groups them;
 *  - a SINGLE per-pair key (`SILO_V2_<config>`), which is what a caller naming one
 *    market passes, and what every existing test does.
 *
 * Dropping the second shape would have been invisible to the suite — it names keys
 * explicitly — and total in production the other way round. That asymmetry is the
 * discovery-coverage trap this repo has hit twice; supporting both costs one branch.
 */
export const resolveSiloRoster = (
  chainId: string,
  lender: string,
  params?: any,
): SiloRosterEntry[] => {
  const v3 = isSiloV3Type(lender)
  const entryFor = v3 ? getSiloV3MarketEntry : getSiloV2MarketEntry
  const keyFor = v3 ? siloV3LenderKey : siloV2LenderKey

  // Collapsed: `params` is the list of per-pair keys this query stands in for.
  if (Array.isArray(params) && params.length > 0) {
    const out: SiloRosterEntry[] = []
    for (const key of params) {
      const market = entryFor(chainId, key)
      if (market) out.push({ key, market })
    }
    return out
  }

  // Single per-pair key.
  const single = entryFor(chainId, lender)
  if (single) return [{ key: lender, market: single }]

  // Bare prefix with no params — the whole chain. Not issued by production today,
  // but the honest answer for `SILO_V2` on its own, and it keeps a caller that
  // collapses queries WITHOUT populating params from silently reading nothing.
  const all = (v3 ? siloMarketsV3() : siloMarkets())?.[chainId] ?? []
  return all.map((market) => ({ key: keyFor(market.siloConfig), market }))
}
